import { HttpClient, type RequestOptions } from '@core/api-clients/HttpClient'
import HttpError from '@core/api-clients/HttpError'

import type { AuthenticationServiceInterface } from '../types'
import type { JSendResponse } from './ApiClient.types'
import { ApiError } from './ApiError'

export interface ExtendedRequestOptions extends RequestOptions {
  auth?: boolean
}

/**
 * Extends the standard HTTP client to work with the operator API, adding:
 *
 * • Supports JSend envelope for HTTP responses, unwrapping response data from
 *   the result or, in the event of an problem, throwing contextual errors.
 *
 * • Opt-in authentication support, provided by an AuthenticationService.
 *
 * • Sets the default `X-Integrator-Key-Id` and `X-Site-Id` headers.
 */
export default class ApiClient extends HttpClient {
  constructor(
    host: string,
    operatorKeyId: string,
    siteId: string,
    private authenticator?: AuthenticationServiceInterface
  ) {
    super({
      baseUrl: `${host}/player/competitions/v1`,
      defaultHeaders: {
        'X-Integrator-Key-Id': operatorKeyId,
        'X-Site-Id': siteId,
      },
    })
  }

  private async applyAuthenticationOptions(options: ExtendedRequestOptions) {
    const token = await this.authenticator?.getToken()
    if (token) {
      options.headers = new Headers(options.headers)
      options.headers.set('Authorization', `Bearer ${token}`)
    }
    // TODO: If there's no token we could throw a HttpStatus Error (401) here
    // to save hitting the API with a request we know will fail.
  }

  public async get<TResponse = any>(
    path: string,
    options: Omit<ExtendedRequestOptions, 'method' | 'body'> = {}
  ): Promise<TResponse> {
    if (options.auth) {
      await this.applyAuthenticationOptions(options)
    }
    return this.usingJsend(() =>
      super.get<JSendResponse<TResponse>>(path, options)
    )
  }

  public async post<TResponse = any, TRequest = any>(
    path: string,
    body: TRequest,
    options: Omit<ExtendedRequestOptions, 'method' | 'body'> = {}
  ): Promise<TResponse> {
    if (options.auth) {
      await this.applyAuthenticationOptions(options)
    }
    return this.usingJsend(() =>
      super.post<JSendResponse<TResponse>>(path, body, options)
    )
  }

  private async usingJsend<T = any>(
    fn: () => Promise<JSendResponse<T>>
  ): Promise<T> {
    try {
      const result = await fn()
      return this.parseJsend(result)
    } catch (e) {
      if (e instanceof HttpError) {
        return this.parseJsend(e.data as any)
      }
      throw e
    }
  }

  private parseJsend<T>(json: JSendResponse): T {
    if (json.status === 'success') {
      return json.data
    }
    if (json.status === 'fail') {
      throw new ApiError(json.data)
    }
    if (json.status === 'error') {
      throw new ApiError(json.message)
    }
    throw new ApiError('Invalid JSend Response')
  }
}
